import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactTable from 'components/ReactTable';
import { getWhdFormUploadsByObject, showAddWhsDocumentsModal, hideAddWhsDocumentsModal, submitAddWhsDocuments,
  showDeleteFormModal, hideDeleteFormModal, hardDeleteForms,
  showEditFormTypeModal, hideEditFormTypeModal, updateFormType,
  hideReassignWhsDocumentModal, showReassignWhsDocumentModal, reassignWhsDocumentForm } from 'actions';

import AddWhsDocumentsModal from 'components/Modals/AddWhsDocumentsModal';
import DeleteFormModal from 'components/Modals/DeleteFormModal';
import UpdateFormTypeModal from 'components/Modals/UpdateFormTypeModal';
import ReassignWhsDocumentModal from 'components/Modals/ReassignWhsDocumentModal';

import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import statementFormsTabColumnDefs from './statementFormsTabColumnDefs';
import formsTabActions from './statementFormsTabActions';

import ActionSelectDelete from './ActionSelectDelete';

const StatementFormsTab = (props) => {
  const { dispatch, permissions, accountId, gridTotalLimit,
    forms, formsCount, isAddWhsDocumentsModalShown,
    isDeleteFormModalShown, isDeletingForm, deleteFormError,
    isEditFormTypeModalShown, editingFormTypeId, editingFormTypeOriginalFileName, editingFormTypeOtherFormTypesInFamily,
    isReassignWhsDocumentModalShown, reassignWhsDocumentFormId, reassignWhsDocumentFormOriginalFileName, boDbIdToName } = props;
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [gridCriteria, setGridCriteria] = useState({
    columnDefs: statementFormsTabColumnDefs().concat([]),
    pageIndex: 0,
    sortBy: [],
    filters: [],
    pageCount: 0,
    pageSize: 50,
    itemCount: 0,
    totalLimit: gridTotalLimit,
    selectedItems: [],
    selectedItem: null
  });
  const loadingTime = 500;

  const generateRequest = () => {
    let sortDirection;
    if (gridCriteria.sortBy != null && gridCriteria.sortBy[0]) {
      if (gridCriteria.sortBy[0]?.desc != null) {
        sortDirection = gridCriteria.sortBy[0]?.desc ? 'desc' : 'asc';
      }
    } else {
      sortDirection = null;
    }

    return {
      pageNumber: gridCriteria.pageIndex + 1,
      pageSize: gridCriteria.pageSize,
      totalLimit: gridCriteria.totalLimit,
      sortByField: gridCriteria.sortBy != null ? gridCriteria.sortBy[0]?.id : null,
      sortDirection,
      filterList: gridCriteria.filters,
      accountId
    };
  };

  const sendDataRequest = () => {
    const request = generateRequest();
    dispatch(getWhdFormUploadsByObject(request));
  };

  const refreshButtonClicked = () => {
    setLoading(true);
    setGridCriteria(prev => ({
      ...prev,
      selectedItems: [],
      selectedItem: null
    }));
    sendDataRequest();
  };

  const hideLoadingAnimation = () => {
    const { isGettingForms } = props;
    const loadingInterval = setInterval(() => {
      if (!isGettingForms) {
        setLoading(false);
        clearInterval(loadingInterval);
      }
    }, loadingTime);
  };

  const processData = (incomingDocuments, incomingDocumentsCount) => {
    const nextData = [];
    if (incomingDocuments != null) {
      incomingDocuments.forEach((document) => {
        let formValidityValue = 'N/A';
        if (document.formValidity === true) {
          formValidityValue = 'Valid';
        } else if (document.formValidity === false) {
          formValidityValue = 'Invalid';
        }

        const datum = {
          itemId: document.id,
          id: document.id,
          documentUploadDate: document.documentUploadDate,
          documentUploadDateDisplay: document.documentUploadDate != null ? moment(document.documentUploadDate).format('DD MMM YYYY') : 'N/A',
          originalFileName: document.originalFileName || 'N/A',
          sourcePath: document.sourcePath || 'N/A',
          formType: document.formType || 'N/A',
          formTypeDescription: document.formTypeDescription || 'N/A',
          otherFormTypesInFamily: document.otherFormTypesInFamily,
          formValidity: formValidityValue,
          workflowStageDescriptionAlias: document.workflowStageDescriptionAlias || 'N/A',
          boName: document.boName,
          isOnlineForm: document.isOnlineForm,
          isForm: document.isForm,
          hasReviewBegan: document.hasReviewBegan || false,
          accountId,
        };
        nextData.push(datum);
      });
      const pageCount = Math.ceil(incomingDocumentsCount / gridCriteria.pageSize);
      setData(nextData);
      setGridCriteria(prev => ({
        ...prev,
        itemCount: incomingDocumentsCount,
        pageCount,
        totalLimit: gridTotalLimit
      }));
    }
  };

  useEffect(() => {
    if (forms && formsCount >= 0) {
      processData(forms, formsCount);
      hideLoadingAnimation();
      if (!initialLoaded) {
        setInitialLoaded(true);
      }
    }
  }, [forms]);

  useEffect(() => {
    sendDataRequest();
  }, []);

  const onGridCriteriaChanged = (newGridCriteria) => {
    setGridCriteria({ ...newGridCriteria });
  };

  const isQuickFilterActive = (filterId, filterValue) => gridCriteria.filters
    .some(f => f.id === filterId && f.value?.filter?.[0] === filterValue);

  const applyQuickFilter = (filter) => {
    if (filter === 'INVALID_FORMS') {
      const isActive = isQuickFilterActive('formValidity', 'false');
      const newFilters = gridCriteria.filters.filter(f => f.id !== 'formValidity');
      if (!isActive) {
        newFilters.push({ id: 'formValidity', value: { filter: ['false'], filterType: 'bool' } });
      }
      const newGridCriteria = {
        ...gridCriteria,
        filters: newFilters,
        pageIndex: 0
      };
      onGridCriteriaChanged(newGridCriteria);
    } else if (filter === 'UNASSIGNED') {
      const isActive = isQuickFilterActive('boName', 'Unassigned');
      const newFilters = gridCriteria.filters.filter(f => f.id !== 'boName');
      if (!isActive) {
        newFilters.push({ id: 'boName', value: { filter: ['Unassigned'], filterType: 'text' } });
      }
      const newGridCriteria = {
        ...gridCriteria,
        filters: newFilters,
        pageIndex: 0
      };
      onGridCriteriaChanged(newGridCriteria);
    }
  };

  useEffect(() => {
    sendDataRequest();
  }, [gridCriteria.pageIndex, gridCriteria.sortBy, gridCriteria.filters, gridCriteria.pageSize]);

  const addWhsDocumentAction = (uploadedDocs) => {
    dispatch(submitAddWhsDocuments(accountId, uploadedDocs));
  };

  const openAddDocumentModal = () => {
    dispatch(showAddWhsDocumentsModal());
  };

  const closeAddDocumentModal = () => {
    dispatch(hideAddWhsDocumentsModal());
  };

  useEffect(() => {
    if (isAddWhsDocumentsModalShown === false) {
      sendDataRequest();
    }
  }, [isAddWhsDocumentsModalShown]);

  const openDeleteFormModal = (item) => {
    const selectedObjects = [];
    if (!item) {
      for (let i = 0; i < gridCriteria.selectedItems.length; i += 1) {
        const listItem = gridCriteria.selectedItems[i].isForm === true ? gridCriteria.selectedItems[i] : null;
        if (listItem) {
          selectedObjects.push({ id: listItem.id });
        }
      }
    } else {
      setGridCriteria(prev => ({ ...prev, selectedItem: item }));
      selectedObjects.push({ id: item.id });
    }
    if (selectedObjects.length > 0) {
      dispatch(showDeleteFormModal(selectedObjects));
    }
  };

  const closeDeleteFormModal = () => {
    dispatch(hideDeleteFormModal());
  };

  const openEditFormTypeModal = (row) => {
    dispatch(showEditFormTypeModal(row.id, row.originalFileName, row.otherFormTypesInFamily));
  };

  const closeEditFormTypeModal = () => {
    dispatch(hideEditFormTypeModal());
  };

  const processUpdateFormType = (formId, newFormType) => {
    dispatch(
      updateFormType(formId, newFormType, () => {
        sendDataRequest();
      })
    );
    closeEditFormTypeModal();
  };

  const processDeleteForms = () => {
    if (gridCriteria.selectedItem != null) {
      // if selectedItem exists, then delete that item (this is the item for the row with the delete icon that was pressed)
      dispatch(
        hardDeleteForms(
          [gridCriteria.selectedItem.id],
          true,
          () => {
            setGridCriteria(prev => ({ ...prev, selectedItem: null }));
            sendDataRequest();
          }
        )
      );
    } else {
      // otherwise, delete all the selectedItems
      const filteredSelectedItemsIds = gridCriteria.selectedItems.filter(x => x.isForm === true);
      const selectedItemsIds = Array.from(filteredSelectedItemsIds, x => x.id);
      dispatch(
        hardDeleteForms(selectedItemsIds, true, () => {
          setGridCriteria(prev => ({ ...prev, selectedItems: [] }));
          sendDataRequest();
        })
      );
    }
    closeDeleteFormModal();
  };

  const onRowSelectionChanged = (rowSelections) => {
    setGridCriteria({ ...gridCriteria, ...rowSelections });
  };

  const openReassignWhsDocumentModal = (row) => {
    dispatch(showReassignWhsDocumentModal(row.id, row.originalFileName));
  };

  const closeReassignWhsDocumentModal = () => {
    dispatch(hideReassignWhsDocumentModal());
  };

  const processReassignWhsDocument = (formId, newBoId) => {
    dispatch(
      reassignWhsDocumentForm(formId, newBoId, () => {
        sendDataRequest();
      })
    );
    closeReassignWhsDocumentModal();
  };

  const getActionsColumn = () => formsTabActions({ permissions, dispatch, openDeleteFormModal, openEditFormTypeModal, openReassignWhsDocumentModal });

  return (
    <div className="landing-page documents-tab-content">
      <div className="panel" id="statement-forms-tab-panel">
        <div className="panel-body results form-results">
          <div className="table-header">
            <div className="quick-filters">
              Quick Filters:
              <button
                type="button"
                className={ `btn btn-secondary${ isQuickFilterActive('formValidity', 'false') ? ' active' : '' }` }
                id="invalid-forms-quick-filter-btn"
                title="Invalid Forms"
                onClick={ () => applyQuickFilter('INVALID_FORMS') }
              >
                Invalid Forms
              </button>
              <button
                type="button"
                className={ `btn btn-secondary${ isQuickFilterActive('boName', 'Unassigned') ? ' active' : '' }` }
                id="unassigned-quick-filter-btn"
                title="Unassigned"
                onClick={ () => applyQuickFilter('UNASSIGNED') }
              >
                Unassigned
              </button>
            </div>
            <div className="right-align-actions">
              <span className="supporting-documents-note-label">
                Note: Files that are not identified as forms are saved as Supporting Documents on the account and will not appear on the grid below.
              </span>
              <span className="refresh-button">
                <button
                  type="button"
                  className="btn btn-secondary"
                  title="Refresh Grid"
                  id="refresh-grid-button"
                  onClick={ () => refreshButtonClicked() }
                >
                  <span className="icon icon-refresh" />
                </button>
              </span>
              <span className="add-button">
                <button
                  type="button"
                  className="btn btn-secondary"
                  title="Add Documents"
                  id="add-documents-button"
                  onClick={ () => openAddDocumentModal() }
                >
                  Add Documents
                </button>
              </span>
              <span className="bulk-delete">
                <ActionSelectDelete
                  selectedItems={ gridCriteria.selectedItems }
                  openDeleteFormModal={ openDeleteFormModal }
                />
              </span>
            </div>
          </div>
          <div className="table">
            <ReactTable
              data={ data }
              gridCriteria={ gridCriteria }
              onGridCriteriaChanged={ onGridCriteriaChanged }
              onRowSelectionChanged={ onRowSelectionChanged }
              actions={ getActionsColumn() }
              loading={ loading }
              initialLoaded={ initialLoaded }
              className="workflow"
              enableSelection
              id="statement-forms-tab-grid"
            />
          </div>
        </div>
      </div>
      <AddWhsDocumentsModal
        isShown={ isAddWhsDocumentsModalShown }
        onConfirmUpload={ (docs) => { addWhsDocumentAction(docs); } }
        onCancel={ () => closeAddDocumentModal() }
        accountId={ accountId }
      />
      <DeleteFormModal
        isShown={ isDeleteFormModalShown }
        isDeletingForm={ isDeletingForm }
        onConfirmDelete={ () => processDeleteForms() }
        onCancel={ () => closeDeleteFormModal() }
        deleteFormError={ deleteFormError }
      />
      <UpdateFormTypeModal
        isShown={ isEditFormTypeModalShown }
        onSubmitNewFormType={ (formId, newFormType) => processUpdateFormType(formId, newFormType) }
        onCancel={ () => closeEditFormTypeModal() }
        formId={ editingFormTypeId }
        originalFileName={ editingFormTypeOriginalFileName }
        otherFormTypesInFamily={ editingFormTypeOtherFormTypesInFamily }
      />
      <ReassignWhsDocumentModal
        isShown={ isReassignWhsDocumentModalShown }
        onSubmit={ (formId, newBoId) => processReassignWhsDocument(formId, newBoId) }
        onCancel={ () => closeReassignWhsDocumentModal() }
        formId={ reassignWhsDocumentFormId }
        originalFileName={ reassignWhsDocumentFormOriginalFileName }
        boDbIdToName={ boDbIdToName }
        accountId={ accountId }
      />
    </div>
  );
};

StatementFormsTab.propTypes = {
  gridTotalLimit: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  accountId: PropTypes.number.isRequired,
  isGettingForms: PropTypes.bool,
  forms: PropTypes.arrayOf(PropTypes.shape()),
  formsCount: PropTypes.number,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isAddWhsDocumentsModalShown: PropTypes.bool,
  isDeleteFormModalShown: PropTypes.bool,
  isDeletingForm: PropTypes.bool,
  deleteFormError: PropTypes.string,
  isEditFormTypeModalShown: PropTypes.bool,
  editingFormTypeId: PropTypes.number,
  editingFormTypeOriginalFileName: PropTypes.string,
  editingFormTypeOtherFormTypesInFamily: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.number,
        description: PropTypes.string
      })
    ])
  ),
  isReassignWhsDocumentModalShown: PropTypes.bool,
  reassignWhsDocumentFormId: PropTypes.number,
  reassignWhsDocumentFormOriginalFileName: PropTypes.string,
  boDbIdToName: PropTypes.objectOf(PropTypes.string)
};

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
    },
    forms: {
      gridTotalLimit,
      isDeleteFormModalShown,
      isDeletingForm,
      deleteFormError,
      isEditFormTypeModalShown,
      editingFormTypeId,
      editingFormTypeOriginalFileName,
      editingFormTypeOtherFormTypesInFamily
    },
    withholdingStatement: {
      isGettingForms,
      forms,
      formsCount,
      isAddWhsDocumentsModalShown,
      isReassignWhsDocumentModalShown,
      reassignWhsDocumentFormId,
      reassignWhsDocumentFormOriginalFileName,
      statement = {},
      topAccount = {}
    }
  } = state;

  const { beneficialOwners = [] } = statement;

  let boDbIdToName = beneficialOwners.reduce((acc, { customerDbId, name }) => {
    if (customerDbId != null) {
      acc[customerDbId] = name;
    }
    return acc;
  }, {});

  const additionalBos = (topAccount.customers || []).reduce((acc, { customerDbId, name }) => {
    if (customerDbId != null) {
      acc[customerDbId] = `TOP LEVEL - ${ name }`;
    }
    return acc;
  }, {});

  boDbIdToName = { ...boDbIdToName, ...additionalBos };

  return {
    gridTotalLimit,
    isGettingForms,
    forms,
    formsCount,
    permissions,
    isAddWhsDocumentsModalShown,
    isDeleteFormModalShown,
    isDeletingForm,
    deleteFormError,
    isEditFormTypeModalShown,
    editingFormTypeId,
    editingFormTypeOriginalFileName,
    editingFormTypeOtherFormTypesInFamily,
    isReassignWhsDocumentModalShown,
    reassignWhsDocumentFormId,
    reassignWhsDocumentFormOriginalFileName,
    boDbIdToName
  };
}

StatementFormsTab.defaultProps = {
  isGettingForms: false,
  forms: [],
  formsCount: -1,
  permissions: null,
  isAddWhsDocumentsModalShown: false,
  isDeleteFormModalShown: false,
  isDeletingForm: false,
  deleteFormError: null,
  isEditFormTypeModalShown: false,
  editingFormTypeId: 0,
  editingFormTypeOriginalFileName: null,
  editingFormTypeOtherFormTypesInFamily: {},
  isReassignWhsDocumentModalShown: false,
  reassignWhsDocumentFormId: 0,
  reassignWhsDocumentFormOriginalFileName: '',
  boDbIdToName: {}
};

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(StatementFormsTab, 'StatementFormsTab'));
